import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HARBOURS, VHF_INFO, COASTGUARD_REGIONS, CALL_PROCEDURES, type Harbour } from '../lib/harbour-data';

const RATING_STARS = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

type CoastguardRegion = (typeof COASTGUARD_REGIONS)[number];

const coastguardRegionDomId = (region: string) =>
  `coastguard-${region.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;

const HAZARD_LEVEL: Record<string, { color: string; bg: string; label: string }> = {
  bar: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', label: '⚠️ Bar Crossing' },
  river: { color: '#f97316', bg: 'rgba(249,115,22,0.08)', label: 'River Entrance' },
  harbour: { color: '#22c55e', bg: 'rgba(34,197,94,0.08)', label: 'Sheltered Harbour' },
  marina: { color: '#06b6d4', bg: 'rgba(6,182,212,0.08)', label: 'Marina' },
};

function HazardBadge({ type }: { type: string }) {
  const h = HAZARD_LEVEL[type] || HAZARD_LEVEL.harbour;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.65rem] font-bold"
      style={{ background: h.bg, color: h.color, border: `1px solid ${h.color}30` }}
    >
      {h.label}
    </span>
  );
}

const COASTGUARD_MAP_POINTS: Record<string, { x: number; y: number; short: string }> = {
  'Northland & Bay of Islands': { x: 286, y: 92, short: 'Northland' },
  'Auckland / Hauraki Gulf': { x: 276, y: 178, short: 'Auckland' },
  'Waikato / Raglan / West Coast': { x: 214, y: 238, short: 'Waikato' },
  'Bay of Plenty / Coromandel': { x: 326, y: 210, short: 'Bay of Plenty' },
  "East Coast / Gisborne / Hawke's Bay": { x: 348, y: 286, short: 'East Coast' },
  Taranaki: { x: 190, y: 300, short: 'Taranaki' },
  'Wellington / Cook Strait': { x: 238, y: 382, short: 'Wellington' },
  'Marlborough Sounds / Tasman Bay': { x: 190, y: 438, short: 'Marlborough' },
  'West Coast / South Island': { x: 128, y: 500, short: 'West Coast' },
  Canterbury: { x: 202, y: 540, short: 'Canterbury' },
  Otago: { x: 160, y: 590, short: 'Otago' },
  'Southland / Fiordland': { x: 108, y: 602, short: 'Southland' },
};

function NZCoastguardMap({
  regions,
  selectedRegion,
  onSelect,
}: {
  regions: CoastguardRegion[];
  selectedRegion: string | null;
  onSelect: (region: string) => void;
}) {
  return (
    <section className="rounded-3xl border border-[rgba(16,42,66,0.09)] bg-surface-light dark:bg-surface overflow-hidden shadow-sm">
      <div className="px-4 md:px-5 py-3 md:py-4 border-b border-[rgba(16,42,66,0.09)] flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-sm md:text-base font-extrabold">NZ Coastguard Regions</h2>
          <p className="text-text-muted text-[0.7rem] md:text-xs mt-0.5">
            Tap a region marker to jump straight to the local Coastguard radio details.
          </p>
        </div>
        <span className="flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[0.65rem] font-bold bg-accent/10 text-accent border border-accent/20">
          {regions.length} regions
        </span>
      </div>

      <div className="grid md:grid-cols-[minmax(0,1fr)_300px]">
        <div className="relative p-2 md:p-4 bg-[radial-gradient(circle_at_35%_20%,rgba(6,182,212,0.10),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(34,197,94,0.08),transparent_40%)]">
          <svg
            viewBox="0 0 420 640"
            className="w-full h-auto max-h-[540px] mx-auto"
            role="img"
            aria-labelledby="nz-coastguard-map-title nz-coastguard-map-desc"
          >
            <title id="nz-coastguard-map-title">New Zealand Coastguard regions map</title>
            <desc id="nz-coastguard-map-desc">
              Simplified New Zealand map with clickable markers for each Coastguard region.
            </desc>
            <defs>
              <linearGradient id="nz-coastguard-land" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(34,197,94,0.16)" />
                <stop offset="100%" stopColor="rgba(6,182,212,0.10)" />
              </linearGradient>
            </defs>

            <path
              d="M259 42 C306 52 344 92 358 148 C374 210 356 278 318 334 C286 381 244 414 204 424 C184 429 166 418 160 398 C152 372 164 344 184 318 C210 284 220 244 210 198 C199 145 216 82 259 42 Z"
              fill="url(#nz-coastguard-land)"
              stroke="rgba(79,179,217,0.45)"
              strokeWidth="2"
            />
            <path
              d="M164 438 C206 428 244 452 258 498 C272 545 252 596 210 622 C176 642 132 632 108 600 C84 568 88 514 116 480 C130 462 144 444 164 438 Z"
              fill="url(#nz-coastguard-land)"
              stroke="rgba(79,179,217,0.45)"
              strokeWidth="2"
            />
            <path
              d="M132 632 L158 640 L124 640 Z"
              fill="rgba(6,182,212,0.12)"
              stroke="rgba(79,179,217,0.35)"
              strokeWidth="1.5"
            />

            {regions.map((region, i) => {
              const point = COASTGUARD_MAP_POINTS[region.region] ?? { x: 210 + (i % 3) * 42, y: 120 + i * 38, short: region.region };
              const selected = selectedRegion === region.region;
              return (
                <g
                  key={region.region}
                  role="button"
                  tabIndex={0}
                  aria-label={`Show ${region.region} Coastguard details`}
                  aria-pressed={selected}
                  className="cursor-pointer focus:outline-none"
                  onClick={() => onSelect(region.region)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(region.region);
                    }
                  }}
                >
                  <title>{`${region.region} — ${region.coastguardUnit}`}</title>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={selected ? 15 : 12}
                    fill={selected ? 'var(--accent)' : 'rgba(79,179,217,0.16)'}
                    stroke="var(--accent)"
                    strokeWidth={selected ? 3 : 2}
                  />
                  <text
                    x={point.x}
                    y={point.y + 4}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="800"
                    fill={selected ? '#ffffff' : 'var(--accent)'}
                  >
                    {i + 1}
                  </text>
                  <text
                    x={point.x + 18}
                    y={point.y + 4}
                    fontSize="11"
                    fontWeight="700"
                    fill="var(--text-secondary)"
                    className="hidden md:block"
                  >
                    {point.short}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="border-t md:border-t-0 md:border-l border-[rgba(16,42,66,0.09)] p-3 md:p-4 space-y-2 max-h-[540px] overflow-y-auto">
          {regions.map((region, i) => {
            const selected = selectedRegion === region.region;
            return (
              <button
                key={region.region}
                type="button"
                aria-pressed={selected}
                onClick={() => onSelect(region.region)}
                className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all ${
                  selected
                    ? 'border-accent/40 bg-accent/10 shadow-sm'
                    : 'border-[rgba(16,42,66,0.09)] bg-surface dark:bg-surface-dark hover:border-accent/25'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className={`flex-shrink-0 w-7 h-7 rounded-lg border flex items-center justify-center text-[0.7rem] font-extrabold ${
                      selected ? 'bg-accent text-white dark:text-bg border-accent' : 'bg-accent/10 text-accent border-accent/20'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-xs truncate">{region.region}</span>
                    <span className="block text-text-muted text-[0.65rem] truncate">{region.coastguardUnit}</span>
                  </span>
                  <span className="flex-shrink-0 text-[0.65rem] font-bold text-accent">Ch {region.vhfChannel}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HarbourCard({ harbour, index, onClick }: { harbour: Harbour; index: number; onClick: () => void }) {
  const h = HAZARD_LEVEL[harbour.entranceType] || HAZARD_LEVEL.harbour;
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, type: 'spring', stiffness: 100, damping: 15 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className="w-full h-full text-left rounded-2xl p-4 md:p-5 border overflow-hidden relative md:transition-transform md:duration-200 md:hover:-translate-y-0.5 md:hover:shadow-lg"
      style={{
        background: h.bg,
        borderColor: `${h.color}20`,
        transformStyle: 'preserve-3d',
        perspective: '600px',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm md:text-base font-bold truncate">{harbour.name}</div>
          <div className="text-text-muted text-[0.65rem] md:text-xs">{harbour.region} · {harbour.island} Island</div>
        </div>
        <HazardBadge type={harbour.entranceType} />
      </div>
      <p className="text-text-secondary text-[0.7rem] md:text-xs leading-snug line-clamp-2 mb-2">{harbour.description}</p>
      <div className="flex items-center gap-3 text-[0.65rem] md:text-xs text-text-muted">
        <span>📡 VHF Ch {harbour.vhfChannel}</span>
        {harbour.barCrossing && <span className="text-red-500 font-semibold">⚠️ Bar Crossing</span>}
      </div>
    </motion.button>
  );
}

function HarbourDetail({ harbour, onClose }: { harbour: Harbour; onClose: () => void }) {
  const h = HAZARD_LEVEL[harbour.entranceType] || HAZARD_LEVEL.harbour;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[500px] md:max-w-[780px] max-h-[85vh] overflow-y-auto rounded-t-3xl md:rounded-3xl bg-surface-light dark:bg-surface border-t-4 md:border border-[rgba(79,179,217,0.2)] shadow-2xl"
        style={{ borderTopColor: h.color }}
      >
        {/* Drag handle */}
        <div className="sticky top-0 bg-surface-light dark:bg-surface pt-3 pb-2 px-4 z-10 rounded-t-3xl">
          <div className="w-10 h-1 bg-text-muted/30 rounded-full mx-auto mb-3" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-extrabold">{harbour.name}</h2>
              <div className="text-text-muted text-xs">{harbour.region} · {harbour.island} Island</div>
            </div>
            <button onClick={onClose} className="flex-shrink-0 w-8 h-8 rounded-full bg-surface dark:bg-surface-dark flex items-center justify-center text-text-secondary hover:text-text-primary">
              ✕
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <HazardBadge type={harbour.entranceType} />
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.65rem] font-bold bg-accent/10 text-accent border border-accent/20">
              📡 VHF Ch {harbour.vhfChannel}
            </span>
            {harbour.barCrossing && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.65rem] font-bold bg-red-500/10 text-red-500 border border-red-500/20">
                ⚠️ Bar Crossing
              </span>
            )}
          </div>
        </div>

        <div className="px-4 md:px-6 pb-6 space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
          {/* Description */}
          <div className="md:col-span-2">
            <p className="text-text-secondary text-sm md:text-[0.95rem] leading-relaxed">{harbour.description}</p>
          </div>

          {/* Approach */}
          <div className="rounded-xl bg-surface dark:bg-surface-dark p-4">
            <h3 className="font-semibold text-xs md:text-sm mb-2 flex items-center gap-1.5">🧭 Approach</h3>
            <p className="text-text-secondary text-[0.75rem] md:text-sm leading-relaxed">{harbour.approach}</p>
          </div>

          {/* Leading lights */}
          <div className="rounded-xl bg-surface dark:bg-surface-dark p-4">
            <h3 className="font-semibold text-xs md:text-sm mb-2 flex items-center gap-1.5">💡 Leading Lights & Marks</h3>
            <p className="text-text-secondary text-[0.75rem] md:text-sm leading-relaxed">{harbour.leadingLights}</p>
          </div>

          {/* Tide notes */}
          <div className="rounded-xl bg-accent/5 border border-accent/15 p-4">
            <h3 className="font-semibold text-xs md:text-sm mb-2 flex items-center gap-1.5">🌊 Tide Notes</h3>
            <p className="text-text-secondary text-[0.75rem] md:text-sm leading-relaxed">{harbour.tideNotes}</p>
          </div>

          {/* Hazards */}
          <div className="rounded-xl bg-red-500/5 border border-red-500/15 p-4">
            <h3 className="font-semibold text-xs mb-2 flex items-center gap-1.5 text-red-500">⚠️ Hazards</h3>
            <ul className="space-y-1.5">
              {harbour.hazards.map((hazard, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="text-text-secondary text-[0.75rem] md:text-sm leading-relaxed flex items-start gap-2"
                >
                  <span className="text-red-500 flex-shrink-0">•</span>
                  <span>{hazard}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Emergency */}
          <div className="rounded-xl bg-surface dark:bg-surface-dark p-4 md:col-span-2">
            <h3 className="font-semibold text-xs md:text-sm mb-2 flex items-center gap-1.5">🆘 Emergency & Coastguard</h3>
            <div className="space-y-1">
              <div className="text-text-secondary text-[0.75rem] md:text-sm">{harbour.coastguard}</div>
              <div className="text-text-secondary text-[0.75rem] md:text-sm">Emergency: {harbour.emergency}</div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function VHFReference({ selectedRegion }: { selectedRegion: string | null }) {
  const [safetyTab, setSafetyTab] = useState<'vhf' | 'coastguard' | 'calls'>('coastguard');

  useEffect(() => {
    if (!selectedRegion) return;
    setSafetyTab('coastguard');
    const timer = window.setTimeout(() => {
      document
        .getElementById(coastguardRegionDomId(selectedRegion))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [selectedRegion]);

  return (
    <div className="space-y-4">
      {/* Safety sub-tabs */}
      <div className="relative flex gap-2 p-1.5 bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.09)] rounded-xl">
        {([['coastguard','📡 Coastguard'], ['calls','📞 Call Procedures'], ['vhf','📻 VHF Channels']] as const).map(([key,label]) => (
          <button
            key={key}
            onClick={() => setSafetyTab(key)}
            className="relative flex-1 px-3 py-2.5 md:py-3 rounded-lg text-xs md:text-sm font-bold z-10 transition-colors text-center"
            style={{ color: safetyTab === key ? 'var(--accent)' : 'var(--text-secondary)' }}
          >
            {label}
            {safetyTab === key && (
              <motion.div
                layoutId="safety-tab-bg"
                className="absolute inset-0 rounded-lg bg-accent/10 border border-accent/20 -z-10"
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
      {safetyTab === 'coastguard' && (
        <motion.div key="cg" initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -15 }} transition={{ duration: 0.2 }}>
          {/* Coastguard Regional Radio Network */}
          <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
            <div className="bg-surface-light dark:bg-surface px-4 py-2.5 border-b border-[rgba(16,42,66,0.09)]">
              <span className="font-display text-xs font-bold uppercase tracking-wider">📡 Coastguard NZ Regional Radio</span>
            </div>
            <div className="divide-y divide-[rgba(79,179,217,0.05)] md:divide-y-0 md:grid md:grid-cols-2 md:gap-3 md:p-3">
              {COASTGUARD_REGIONS.map((region, i) => (
                <motion.div
                  key={i}
                  id={coastguardRegionDomId(region.region)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className={`px-4 py-4 md:p-5 md:rounded-xl md:border md:border-[rgba(16,42,66,0.09)] scroll-mt-24 transition-shadow ${
                    selectedRegion === region.region ? 'ring-2 ring-accent shadow-lg bg-accent/5' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm md:text-base leading-snug">{region.region}</div>
                      <div className="text-text-muted text-xs md:text-sm leading-snug mt-0.5">{region.coastguardUnit}</div>
                    </div>
                    <span className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.7rem] md:text-xs font-bold bg-accent/10 text-accent border border-accent/20">
                      Ch {region.vhfChannel}
                    </span>
                  </div>
                  <div className="text-text-secondary text-xs md:text-sm leading-relaxed mb-1.5">📡 {region.coverage}</div>
                  <div className="text-text-muted text-[0.7rem] md:text-sm leading-relaxed mb-1.5">🕐 {region.broadcastTimes}</div>
                  <div className="text-text-muted text-[0.7rem] md:text-sm leading-relaxed mb-1.5">🔁 Repeaters: {region.repeaters}</div>
                  <div className="text-text-secondary text-xs md:text-sm leading-relaxed mt-2 pt-2 border-t border-[rgba(79,179,217,0.05)]">
                    💡 {region.contactNote}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {safetyTab === 'calls' && (
        <motion.div key="calls" initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -15 }} transition={{ duration: 0.2 }}>
          {/* Standard Call Procedures */}
          <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
            {CALL_PROCEDURES.map((proc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden"
              >
                <div className="bg-surface-light dark:bg-surface px-4 py-3 md:px-5 border-b border-[rgba(16,42,66,0.09)] flex items-center gap-3">
                  <span className="text-xl">{proc.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-sm md:text-base leading-snug">{proc.situation}</div>
                    <div className="text-text-muted text-xs md:text-sm leading-snug mt-0.5">{proc.when}</div>
                  </div>
                  <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-[0.7rem] md:text-xs font-bold bg-accent/10 text-accent border border-accent/20">
                    {proc.channel}
                  </span>
                </div>
                <div className="px-4 py-4 md:p-5">
                  <ol className="space-y-1.5">
                    {proc.steps.map((step, j) => (
                      <li key={j} className="text-text-secondary text-xs md:text-sm leading-relaxed flex items-start gap-2.5">
                        <span className="text-accent flex-shrink-0 font-bold text-[0.7rem] md:text-sm mt-0.5">{j + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-3 pt-3 border-t border-[rgba(79,179,217,0.05)] text-xs md:text-sm text-text-muted leading-relaxed">
                    💡 {proc.note}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {safetyTab === 'vhf' && (
        <motion.div key="vhf" initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -15 }} transition={{ duration: 0.2 }}>
          {/* VHF Channels */}
          <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
            <div className="bg-surface-light dark:bg-surface px-4 py-3 border-b border-[rgba(16,42,66,0.09)]">
              <span className="font-display text-sm font-bold uppercase tracking-wider">📻 VHF Channels</span>
            </div>
            <div className="divide-y divide-[rgba(79,179,217,0.05)] md:divide-y-0 md:grid md:grid-cols-2 md:gap-3 md:p-3">
              {VHF_INFO.channels.map((ch, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="px-4 py-4 md:p-4 flex items-center gap-3 md:rounded-xl md:border md:border-[rgba(16,42,66,0.09)]"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center font-bold text-base text-accent">
                    {ch.channel}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm md:text-base leading-snug">{ch.purpose}</div>
                    <div className="text-text-muted text-xs md:text-sm leading-relaxed mt-0.5">{ch.notes}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Distress calls */}
          <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
            <div className="bg-surface-light dark:bg-surface px-4 py-3 border-b border-[rgba(16,42,66,0.09)]">
              <span className="font-display text-sm font-bold uppercase tracking-wider">🚨 Distress Calls</span>
            </div>
            <div className="divide-y divide-[rgba(79,179,217,0.05)] md:divide-y-0 md:grid md:grid-cols-2 md:gap-3 md:p-3">
              {VHF_INFO.distressCalls.map((call, i) => (
                <div key={i} className="px-4 py-4 md:p-5 md:rounded-xl md:border md:border-[rgba(16,42,66,0.09)]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`font-bold text-base ${call.type === 'MAYDAY' ? 'text-red-500' : call.type === 'PAN-PAN' ? 'text-orange-500' : 'text-yellow-500'}`}>
                      {call.type}
                    </span>
                  </div>
                  <div className="text-text-muted text-xs md:text-sm mb-3">Use when:{call.when}</div>
                  <pre className="text-xs md:text-sm text-text-secondary bg-surface dark:bg-surface-dark rounded-lg p-4 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">{call.format}</pre>
                </div>
              ))}
            </div>
          </div>

          {/* Emergency contacts */}
          <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
            <div className="bg-surface-light dark:bg-surface px-4 py-3 border-b border-[rgba(16,42,66,0.09)]">
              <span className="font-display text-sm font-bold uppercase tracking-wider">🆘 Emergency Contacts</span>
            </div>
            <div className="divide-y divide-[rgba(79,179,217,0.05)] md:divide-y-0 md:grid md:grid-cols-2 md:gap-3 md:p-3">
              {VHF_INFO.emergencyContacts.map((contact, i) => (
                <div key={i} className="px-4 py-4 md:p-4 flex items-center justify-between gap-3 md:rounded-xl md:border md:border-[rgba(16,42,66,0.09)]">
                  <div>
                    <div className="font-semibold text-sm md:text-base leading-snug">{contact.name}</div>
                    <div className="text-text-muted text-xs md:text-sm leading-relaxed mt-0.5">{contact.notes}</div>
                  </div>
                  <div className="font-bold text-base text-accent">{contact.number}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Safety rules */}
          <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden">
            <div className="bg-surface-light dark:bg-surface px-4 py-3 border-b border-[rgba(16,42,66,0.09)]">
              <span className="font-display text-sm font-bold uppercase tracking-wider">📋 Safety Rules</span>
            </div>
            <div className="divide-y divide-[rgba(79,179,217,0.05)] md:divide-y-0 md:grid md:grid-cols-2 md:gap-3 md:p-3">
              {VHF_INFO.rules.map((rule, i) => (
                <div key={i} className="px-4 py-4 md:p-5 md:rounded-xl md:border md:border-[rgba(16,42,66,0.09)]">
                  <div className="font-semibold text-sm md:text-base leading-snug mb-1.5">{rule.rule}</div>
                  <div className="text-text-secondary text-xs md:text-sm leading-relaxed">{rule.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}

export default function HarbourGuide() {
  const [selected, setSelected] = useState<Harbour | null>(null);
  const [island, setIsland] = useState<'all' | 'North' | 'South'>('all');
  const [tab, setTab] = useState<'harbours' | 'safety'>('harbours');
  const [search, setSearch] = useState('');
  const [selectedCoastguardRegion, setSelectedCoastguardRegion] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return HARBOURS.filter(h => {
      if (island !== 'all' && h.island !== island) return false;
      if (search && !h.name.toLowerCase().includes(search.toLowerCase()) && !h.region.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [island, search]);

  const barCount = HARBOURS.filter(h => h.barCrossing).length;

  const handleCoastguardRegionSelect = (region: string) => {
    setSelectedCoastguardRegion(region);
    setTab('safety');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="relative overflow-hidden pt-6 pb-4 px-4">
        <motion.div
          className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(34,197,94,0.06), transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative z-10 max-w-[500px] md:max-w-[860px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-2xl md:text-4xl font-extrabold tracking-tight mb-1 md:mb-2"
          >
            Harbour Guide & Safety
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-secondary text-xs md:text-sm mb-4 md:mb-6"
          >
            {HARBOURS.length} NZ harbours · {barCount} bar crossings · {COASTGUARD_REGIONS.length} Coastguard regions · {CALL_PROCEDURES.length} call procedures
          </motion.p>

          {/* Tab bar */}
          <div className="relative flex gap-1 p-1 bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.09)] rounded-xl max-w-[300px] md:max-w-[440px] mx-auto mb-4">
            {([['harbours', '⚓ Harbours'], ['safety', '🆘 Safety & Radio']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className="relative flex-1 px-4 py-2.5 rounded-lg text-sm font-bold z-10 transition-colors"
                style={{ color: tab === key ? 'var(--accent)' : 'var(--text-secondary)' }}
              >
                {label}
                {tab === key && (
                  <motion.div
                    layoutId="harbour-tab-bg"
                    className="absolute inset-0 rounded-lg bg-accent/10 border border-accent/20 -z-10"
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[500px] md:max-w-[1200px] mx-auto px-4 md:px-8 pb-6">
        <NZCoastguardMap
          regions={COASTGUARD_REGIONS}
          selectedRegion={selectedCoastguardRegion}
          onSelect={handleCoastguardRegionSelect}
        />
      </div>

      <div className="max-w-[500px] md:max-w-[1200px] mx-auto px-4 md:px-8 pb-12">
        <AnimatePresence mode="wait">
          {tab === 'harbours' && (
            <motion.div
              key="harbours"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {/* Search + filter */}
              <div className="mb-4 md:mb-6 space-y-2 md:space-y-0 md:flex md:items-center md:gap-3">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search harbours or regions…"
                  className="form-input w-full md:flex-1 text-sm"
                />
                <div className="flex gap-1 p-1 bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.09)] rounded-lg md:w-[340px] md:flex-none">
                  {(['all', 'North', 'South'] as const).map(isl => (
                    <button
                      key={isl}
                      onClick={() => setIsland(isl)}
                      className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                        island === isl
                          ? 'bg-accent text-white dark:text-bg'
                          : 'text-text-secondary'
                      }`}
                    >
                      {isl === 'all' ? 'All' : `${isl} Island`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Harbour cards */}
              <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4">
                {filtered.map((harbour, i) => (
                  <HarbourCard key={harbour.name} harbour={harbour} index={i} onClick={() => setSelected(harbour)} />
                ))}
                {filtered.length === 0 && (
                  <div className="text-center py-8 text-text-muted text-sm">No harbours found.</div>
                )}
              </div>
            </motion.div>
          )}

          {tab === 'safety' && (
            <motion.div
              key="safety"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              <VHFReference selectedRegion={selectedCoastguardRegion} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Disclaimer */}
        <p className="text-text-muted text-[0.65rem] text-center mt-6 px-4">
          ⚠️ This information is for reference only and may be outdated. Always consult official LINZ charts, Coastguard NZ, and local harbourmaster notices before navigating. Do not rely on this as your sole source of safety information.
        </p>

        <div className="mt-4 text-center">
          <Link to="/" className="text-text-muted text-xs hover:text-accent transition-colors">← Back to MarineLink</Link>
        </div>
      </div>

      {/* Harbour detail modal */}
      <AnimatePresence>
        {selected && <HarbourDetail harbour={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
